import React, { useMemo, useState } from 'react';
import { Loader2, ScanText, ShieldAlert, ShieldCheck, ShieldQuestion, X } from 'lucide-react';
import { useViewModelReady } from '../../../../hooks/useViewModel';
import {
  AiTextDetectionResult,
  AiTextDetectionWindow,
  AuthorshipKind,
  RELIABLE_MIN_WORDS,
  authorshipKind,
  buildHighlightSegments,
  countWords,
  overallKind,
} from '../../../../domain/aiTextDetection';
import { Metric } from './DashboardPanel';
import { MAX_TEXT_CHARS, useDetectionToolViewModel } from './detectionToolViewModel';

const KIND_STYLES: Record<AuthorshipKind, {
  label: string;
  bar: string;
  dot: string;
  highlight: string;
  pill: string;
  text: string;
  iconWrap: string;
  Icon: typeof ShieldAlert;
}> = {
  ai: {
    label: 'AI-generated',
    bar: 'bg-red-500',
    dot: 'bg-red-500',
    highlight: 'bg-red-100 decoration-red-300',
    pill: 'bg-red-50 text-red-700 ring-red-200',
    text: 'text-red-700',
    iconWrap: 'bg-red-50 text-red-600',
    Icon: ShieldAlert,
  },
  assisted: {
    label: 'AI-assisted',
    bar: 'bg-amber-400',
    dot: 'bg-amber-400',
    highlight: 'bg-amber-100 decoration-amber-300',
    pill: 'bg-amber-50 text-amber-700 ring-amber-200',
    text: 'text-amber-700',
    iconWrap: 'bg-amber-50 text-amber-600',
    Icon: ShieldQuestion,
  },
  human: {
    label: 'Human',
    bar: 'bg-emerald-500',
    dot: 'bg-emerald-500',
    highlight: 'bg-emerald-50 decoration-emerald-200',
    pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    text: 'text-emerald-700',
    iconWrap: 'bg-emerald-50 text-emerald-600',
    Icon: ShieldCheck,
  },
};

function percent(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

export function DetectionToolPanel() {
  const vm = useDetectionToolViewModel();
  useViewModelReady(vm.initialize);

  const words = useMemo(() => countWords(vm.text), [vm.text]);
  const chars = vm.text.trim().length;
  const tooLong = chars > MAX_TEXT_CHARS;
  const canAnalyze = chars > 0 && !tooLong && !vm.analyzing;

  if (vm.busy) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-w-0 max-w-5xl">
      <p className="mt-4 max-w-3xl text-sm text-gray-500">
        Paste any text to score it with Pangram. Results are not saved and are not attributed to
        an expert. Each run uses Pangram credits.
      </p>

      <form
        className="mt-5 overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm focus-within:border-primary focus-within:ring-1 focus-within:ring-primary"
        onSubmit={(event) => {
          event.preventDefault();
          if (canAnalyze) void vm.analyze();
        }}
      >
        <label htmlFor="ai-detection-text" className="sr-only">Text to analyze</label>
        <textarea
          id="ai-detection-text"
          value={vm.text}
          onChange={(event) => vm.setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && canAnalyze) {
              event.preventDefault();
              void vm.analyze();
            }
          }}
          rows={10}
          placeholder="Paste text here…"
          className="block w-full resize-y border-0 px-4 py-3 text-sm leading-6 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
        />
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 bg-gray-50 px-4 py-2.5">
          <p className="text-xs tabular-nums text-gray-500">
            {words.toLocaleString()} {words === 1 ? 'word' : 'words'} · {chars.toLocaleString()} characters
            {tooLong && (
              <span className="ml-2 text-red-600">Limit is {MAX_TEXT_CHARS.toLocaleString()} characters</span>
            )}
            {!tooLong && words > 0 && words < RELIABLE_MIN_WORDS && (
              <span className="ml-2 text-amber-700">Under {RELIABLE_MIN_WORDS} words, results are unreliable</span>
            )}
          </p>
          <div className="flex items-center gap-2">
            {(vm.text || vm.result) && (
              <button
                type="button"
                onClick={vm.clear}
                className="inline-flex h-8 items-center gap-1 rounded-md px-2.5 text-xs font-medium text-gray-600 hover:bg-gray-200/60 hover:text-gray-900"
              >
                <X className="h-3.5 w-3.5" />
                Clear
              </button>
            )}
            <button
              type="submit"
              disabled={!canAnalyze}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-white shadow-sm hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {vm.analyzing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ScanText className="h-3.5 w-3.5" />}
              {vm.analyzing ? 'Analyzing…' : 'Analyze'}
            </button>
          </div>
        </div>
      </form>
      <p className="mt-1.5 text-[11px] text-gray-400">Press ⌘/Ctrl + Enter to analyze.</p>

      {vm.error && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {vm.error}
        </div>
      )}

      {vm.result && (
        <div className={`relative mt-6 transition-opacity ${vm.analyzing ? 'opacity-50' : ''}`}>
          <DetectionResult result={vm.result} />
        </div>
      )}
    </div>
  );
}

function DetectionResult({ result }: { result: AiTextDetectionResult }) {
  const [activeWindow, setActiveWindow] = useState<number | null>(null);
  const kind = overallKind(result);
  const style = KIND_STYLES[kind];
  const segmentCount = result.numAiSegments + result.numAiAssistedSegments + result.numHumanSegments;

  return (
    <section aria-label="Detection result">
      <div className="rounded-md border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-start gap-4">
          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${style.iconWrap}`}>
            <style.Icon className="h-6 w-6" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className={`text-lg font-semibold leading-tight ${style.text}`}>
              {result.headline || style.label}
            </h2>
            {result.prediction && <p className="mt-1 text-sm text-gray-600">{result.prediction}</p>}
          </div>
        </div>

        <CompositionBar result={result} />
      </div>

      <dl className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Words scored" value={result.wordCount.toLocaleString()} />
        <Metric
          label="Segments"
          value={segmentCount.toLocaleString()}
          sub={`${result.numAiSegments} AI · ${result.numAiAssistedSegments} assisted · ${result.numHumanSegments} human`}
        />
        <Metric
          label="Model"
          value={result.model}
          sub={result.version ? `version ${result.version}` : undefined}
        />
        <Metric label="Time" value={`${(result.durationMs / 1000).toFixed(1)} s`} />
      </dl>

      {result.windows.length > 0 && (
        <>
          <HighlightedText result={result} activeWindow={activeWindow} onHover={setActiveWindow} />
          <WindowTable windows={result.windows} activeWindow={activeWindow} onHover={setActiveWindow} />
        </>
      )}
    </section>
  );
}

function CompositionBar({ result }: { result: AiTextDetectionResult }) {
  const parts: { kind: AuthorshipKind; fraction: number }[] = [
    { kind: 'ai', fraction: result.fractionAi },
    { kind: 'assisted', fraction: result.fractionAiAssisted },
    { kind: 'human', fraction: result.fractionHuman },
  ];
  return (
    <div className="mt-5">
      <div
        className="flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100"
        role="img"
        aria-label={parts.map((part) => `${KIND_STYLES[part.kind].label} ${percent(part.fraction)}`).join(', ')}
      >
        {parts.map((part) => part.fraction > 0 && (
          <div
            key={part.kind}
            className={KIND_STYLES[part.kind].bar}
            style={{ width: `${part.fraction * 100}%` }}
          />
        ))}
      </div>
      <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
        {parts.map((part) => (
          <div key={part.kind} className="flex items-baseline gap-2">
            <span className={`h-2 w-2 translate-y-[-1px] rounded-full ${KIND_STYLES[part.kind].dot}`} aria-hidden="true" />
            <dt className="text-xs text-gray-500">{KIND_STYLES[part.kind].label}</dt>
            <dd className="text-sm font-semibold tabular-nums text-gray-900">{percent(part.fraction)}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 text-[11px] text-gray-400">
        Shares of text segments by classification, not confidence scores. Dense technical prose is
        flagged incorrectly fairly often.
      </p>
    </div>
  );
}

function HighlightedText({ result, activeWindow, onHover }: {
  result: AiTextDetectionResult;
  activeWindow: number | null;
  onHover: (index: number | null) => void;
}) {
  const segments = useMemo(() => buildHighlightSegments(result.text, result.windows), [result]);
  return (
    <section className="mt-8">
      <div className="border-b border-gray-200 pb-2">
        <h2 className="text-sm font-semibold text-gray-900">Text by segment</h2>
        <p className="mt-0.5 text-xs text-gray-500">Hover a passage to see how Pangram classified it.</p>
      </div>
      <div className="mt-3 max-h-[480px] overflow-y-auto whitespace-pre-wrap rounded-md border border-gray-200 bg-white p-4 text-sm leading-7 text-gray-800">
        {segments.map((segment, position) => {
          if (segment.windowIndex === null) return <span key={position}>{segment.text}</span>;
          const window = result.windows[segment.windowIndex];
          const index = segment.windowIndex;
          return (
            <span
              key={position}
              title={`${window.label || 'Unlabeled'} · ${window.confidence || '—'} confidence · AI score ${window.aiAssistanceScore.toFixed(2)}`}
              onMouseEnter={() => onHover(index)}
              onMouseLeave={() => onHover(null)}
              className={`rounded-sm box-decoration-clone px-0.5 transition-shadow ${KIND_STYLES[authorshipKind(window.label)].highlight} ${
                activeWindow === index ? 'ring-2 ring-primary/60' : ''
              }`}
            >
              {segment.text}
            </span>
          );
        })}
      </div>
    </section>
  );
}

function WindowTable({ windows, activeWindow, onHover }: {
  windows: AiTextDetectionWindow[];
  activeWindow: number | null;
  onHover: (index: number | null) => void;
}) {
  const anyHumanized = windows.some((window) => window.isHumanized);
  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-200 pb-2">
        <h2 className="text-sm font-semibold text-gray-900">Segments</h2>
        <p className="text-[11px] text-gray-500">
          {windows.length.toLocaleString()} {windows.length === 1 ? 'window' : 'windows'}
        </p>
      </div>
      <div className="mt-3 overflow-x-auto rounded-md border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm" aria-label="Pangram segment classifications">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">#</th>
              <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Excerpt</th>
              <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Label</th>
              <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Confidence</th>
              <th scope="col" className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-gray-500">AI score</th>
              <th scope="col" className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Words</th>
              {anyHumanized && (
                <th scope="col" className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Humanizer</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {windows.map((window, index) => {
              const kind = authorshipKind(window.label);
              return (
                <tr
                  key={index}
                  onMouseEnter={() => onHover(index)}
                  onMouseLeave={() => onHover(null)}
                  className={activeWindow === index ? 'bg-gray-50' : undefined}
                >
                  <td className="px-4 py-3 text-right font-mono text-xs tabular-nums text-gray-500">{index + 1}</td>
                  <td className="max-w-[360px] px-4 py-3">
                    <p className="truncate text-gray-700">{window.text}</p>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${KIND_STYLES[kind].pill}`}>
                      {window.label || KIND_STYLES[kind].label}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-600">{window.confidence || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
                        <div
                          className={KIND_STYLES[kind].bar}
                          style={{ width: `${Math.min(1, Math.max(0, window.aiAssistanceScore)) * 100}%`, height: '100%' }}
                        />
                      </div>
                      <span className="font-mono text-xs tabular-nums text-gray-700">{window.aiAssistanceScore.toFixed(2)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs tabular-nums text-gray-700">{window.wordCount.toLocaleString()}</td>
                  {anyHumanized && (
                    <td className="whitespace-nowrap px-4 py-3 text-right text-xs">
                      {window.isHumanized
                        ? <span className="font-medium text-amber-700">Humanized · {window.humanizerScore.toFixed(2)}</span>
                        : <span className="text-gray-400">—</span>}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
